import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FlexWrapper } from '../../theme/common_style';
import ActivityDrawer from './ActivityDrawer';
import { Breadcrumb, Drawer, Table } from 'antd';
import AvatarImage from '../../components/common/AvatarImage';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { projectStatusOption } from '../../utils/constant';
import CopyToClipboard from '../../components/common/CopyToClipboard';
import { useTheme } from '@emotion/react';
import dayjs from 'dayjs';
import { activeStatusTag, generateEmployeeImgUrl, getFullName } from '../../utils/common_functions';
import { AvatarGroup } from '../../components/common/AvatarGroup';
import { updateActivityDrawer } from '../../redux/sidebar/SidebarSlice';
import styled from '@emotion/styled';

const InfoBlock = ({ label, value, jsxValue }) => (
  <FlexWrapper align="flex-start" direction="column" gap="2px" cursor="default">
    <div style={styles.label}>{label}</div>
    <div style={styles.value}>{jsxValue || value || 'N/A'}</div>
  </FlexWrapper>
);

InfoBlock.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  jsxValue: PropTypes.ReactNode
};

const styles = {
  label: {
    fontFamily: 'Plus Jakarta Sans',
    fontSize: '14px',
    fontWeight: '400',
    color: '#9F9F9F'
  },
  value: {
    fontFamily: 'Plus Jakarta Sans',
    fontSize: '16px',
    fontWeight: '500',
    color: '#0E0E0E',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    maxWidth: '95%',
    textAlign: 'left'
  }
};

const ClientInfo = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { client = {} } = location.state || {};
  const activityDrawer = useSelector((state) => state?.sidebar?.isActivityDrawer);
  const theme = useTheme();

  const styles = {
    profileContainer: {
      backgroundColor: 'white',
      padding: '24px',
      borderRadius: '12px',
      alignItems: 'center'
    },
    avatar: {
      width: '160px',
      height: '160px',
      minWidth: '96px',
      borderRadius: '50%',
      backgroundColor: theme.primaryColor,
      fontSize: '44px'
    },
    clientName: {
      fontWeight: '700',
      fontSize: '18px',
      fontFamily: 'Plus Jakarta Sans'
    },
    clientRole: {
      fontWeight: '400',
      fontSize: '14px',
      fontFamily: 'Plus Jakarta Sans'
    },
    infoContainer: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '12px',
      height: '100%'
    },
    infoTitle: {
      width: '100%',
      textAlign: 'start',
      fontFamily: 'Plus Jakarta Sans',
      fontSize: '24px',
      fontWeight: '700'
    },
    label: {
      fontFamily: 'Plus Jakarta Sans',
      fontSize: '14px',
      fontWeight: '400',
      color: '#9F9F9F'
    },
    value: {
      fontFamily: 'Plus Jakarta Sans',
      fontSize: '16px',
      fontWeight: '500',
      color: '#0E0E0E',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      maxWidth: '95%',
      textAlign: 'left'
    }
  };

  useEffect(() => {
    if (!client?.name) {
      navigate('/clients');
    }
  }, [client]);

  const columns = [
    {
      title: 'S.NO',
      dataIndex: 'key',
      key: 'key',
      width: 80,
      render: (_, __, index) => <span>{index + 1}.</span>
    },
    {
      title: 'Project Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <span>{text}</span>
    },
    {
      title: 'Start  Date',
      dataIndex: 'start_date',
      key: 'start_date',
      render: (val) => (
        <div style={{ width: '100px' }}>{val ? dayjs(val).format('DD MMM, YYYY') : 'N/A'}</div>
      )
    },
    {
      title: 'End Date',
      dataIndex: 'deadline',
      key: 'deadline',
      render: (val) => (
        <div style={{ width: '100px' }}>{val ? dayjs(val).format('DD MMM, YYYY') : 'N/A'}</div>
      )
    },
    {
      title: 'Project Manager',
      dataIndex: 'project_Assignee',
      key: 'project_Assignee',
      render: (record) => {
        let projectManager =
          record
            ?.filter((el) => el?.role === 'Project Manager')
            ?.map((val) => {
              let fullName = getFullName(
                val?.emp_id?.first_name,
                val?.emp_id?.middle_name,
                val?.emp_id?.last_name
              );
              return {
                name: fullName,
                src: generateEmployeeImgUrl(val?.emp_id?.id),
                id: val?.emp_id?.id
              };
            }) || [];
        return (
          <FlexWrapper wrap="no-wrap" justify={'start'} gap={'6px'}>
            <AvatarGroup avatars={projectManager} />
            <p style={{ fontSize: '14px', margin: 0 }}>{projectManager?.[0]?.name || 'N/A'}</p>
          </FlexWrapper>
        );
      }
    },
    {
      title: 'Project Incharge',
      dataIndex: 'project_Assignee',
      key: 'project_Assignee',
      render: (incharge) => {
        let projectIncharge =
          incharge
            ?.filter((el) => el?.role === 'Project Incharge')
            ?.map((val) => {
              let fullName = getFullName(
                val?.emp_id?.first_name,
                val?.emp_id?.middle_name,
                val?.emp_id?.last_name
              );

              return {
                name: fullName,
                src: generateEmployeeImgUrl(val?.emp_id?.id),
                id: val?.emp_id?.id
              };
            }) || [];
        return (
          <FlexWrapper wrap="no-wrap" justify={'start'} gap={'6px'}>
            <AvatarGroup avatars={projectIncharge} />
            <p style={{ fontSize: '14px', margin: 0 }}>{projectIncharge?.[0]?.name || 'N/A'}</p>
          </FlexWrapper>
        );
      }
    },
    {
      title: 'Team',
      dataIndex: 'project_Assignee',
      key: 'project_Assignee',
      render: (record) => {
        let projectTeam =
          record
            ?.filter((el) => el?.role === 'Developer')
            ?.map((val) => {
              let fullName = getFullName(
                val?.emp_id?.first_name,
                val?.emp_id?.middle_name,
                val?.emp_id?.last_name
              );
              return {
                name: fullName,
                src: generateEmployeeImgUrl(val?.emp_id?.id),
                id: val?.emp_id?.id
              };
            }) || [];
        return (
          <FlexWrapper justify={'start'} gap={'6px'}>
            <AvatarGroup avatars={projectTeam} />
            {!projectTeam?.[0]?.name && 'N/A'}
          </FlexWrapper>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => activeStatusTag(projectStatusOption, 'value', status)
    }
  ];

  return (
    <FlexWrapper gap="20px" cursor="default">
      {activityDrawer && (
        <Drawer
          width={490}
          title="Activity"
          placement="right"
          closable={true}
          prefixCls="activityCustomDrawer"
          onClose={() => dispatch(updateActivityDrawer(false))}
          open={activityDrawer}
          key="right">
          <ActivityDrawer clientId={client?.id} />
        </Drawer>
      )}
      <FlexWrapper justify="space-between" width="100%" cursor="default">
        <Breadcrumb
          items={[{ title: <Link to="/clients">Client</Link> }, { title: client?.name }]}
        />
      </FlexWrapper>
      <FlexWrapperInfo>
        <FlexWrapper
          className="profile-container"
          direction="column"
          gap="12px"
          cursor="default"
          style={styles.profileContainer}>
          <div style={{ position: 'relative' }}>
            <AvatarImage
              style={styles.avatar}
              image={process.env.REACT_APP_S3_BASE_URL + client?.profile_image}
              name={client?.name}
            />
          </div>
          <FlexWrapper direction="column" align="center" cursor="default">
            <div style={styles.clientName}>{client.name}</div>
          </FlexWrapper>
        </FlexWrapper>

        <FlexWrapper
          className="info-container"
          align="flex-start"
          cursor="default"
          style={styles.infoContainer}
          gap="10px">
          <FlexWrapper justify="space-between" cursor="default" width="100%">
            <div cursor="default" style={styles.infoTitle}>
              Client Info
            </div>
          </FlexWrapper>
          <FlexWrapper direction="column" width="100%" gap="10px" cursor="default">
            <GridBoxs cols={2}>
              <InfoBlock
                label="Email"
                value={client.email}
                jsxValue={
                  <FlexWrapper gap="8px">
                    <span>{client.email || 'N/A'}</span>
                    {client?.email && <CopyToClipboard valueToCopy={client?.email} />}
                  </FlexWrapper>
                }
              />
              <InfoBlock
                label="Contact Number"
                value={`+${client?.country_code} ${client.contact}`}
                jsxValue={
                  <FlexWrapper gap="8px">
                    <span>{`+${client?.country_code} ${client.contact}` || 'N/A'}</span>
                    {client?.contact && (
                      <CopyToClipboard valueToCopy={`+${client?.country_code} ${client.contact}`} />
                    )}
                  </FlexWrapper>
                }
              />
            </GridBoxs>
            <GridBoxs cols={2}>
              <InfoBlock label="Country" value={client.country} />
              <InfoBlock label="State" value={client.state} />
            </GridBoxs>
            <GridBoxs cols={2}>
              <InfoBlock label="City" value={client.city} />
              <InfoBlock label="Address" value={client.address} />
            </GridBoxs>
            <GridBoxs cols={2}>
              {client?.sources?.map((source, index) => (
                <InfoBlock key={index} label={source?.source} value={source?.source_id} />
              ))}
            </GridBoxs>
          </FlexWrapper>
        </FlexWrapper>
      </FlexWrapperInfo>
      {client?.projects?.length > 0 && (
        <FlexWrapper direction="column" width="100%" gap="10px">
          <div style={styles.infoTitle}>Client Projects</div>
          <Table
            prefixCls="antCustomTable"
            columns={columns}
            dataSource={client?.projects}
            pagination={false}
          />
        </FlexWrapper>
      )}
    </FlexWrapper>
  );
};

export default ClientInfo;

const FlexWrapperInfo = styled.div`
  width: 100%;
  display: flex;
  gap: 10px;
  .profile-container {
    width: 30%;
  }
  .info-container {
    width: 68%;
  }
  @media (max-width: 968px) {
    flex-direction: column;
    .profile-container {
      width: 100%;
    }
    .info-container {
      width: 100%;
    }
  }
`;
const GridBoxs = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: ${({ cols }) => `repeat(${cols}, 1fr)`};
  gap: ${({ gap }) => gap || '16px'};
  @media (max-width: 668px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;
